import React, { useEffect, useRef, useCallback, useState } from "react";
import {
  useEditor,
  EditorContent,
  NodeViewWrapper,
  NodeViewContent,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import TiptapImage from "@tiptap/extension-image";
import TiptapLink from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { lowlight } from "lowlight/lib/common.js";
import { Table, TableRow, TableHeader, TableCell } from "@tiptap/extension-table";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Code,
  Link2,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Image,
  Trash2,
  Undo2,
  Redo2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Minus,
  Table as TableIcon,
  ListChecks,
  ChevronDown,
  Check,
  X,
  Plus,
  Rows3,
  Columns3,
  Clipboard,
} from "lucide-react";
import { cn } from "@/lib/utils";

/* ─────────────────────────────────────────────────────────────────────────────
   TABLE STYLES
   ───────────────────────────────────────────────────────────────────────────── */

const TABLE_STYLES = `
.tiptap-editor-root table {
  border-collapse: collapse;
  width: 100%;
  margin: 1rem 0;
  table-layout: fixed;
  overflow: hidden;
}
.tiptap-editor-root table td,
.tiptap-editor-root table th {
  border: 1px solid var(--neutral-300, #d4d4d4);
  padding: 6px 10px;
  min-width: 100px;
  vertical-align: top;
  position: relative;
  box-sizing: border-box;
}
.tiptap-editor-root table th {
  font-weight: 600;
  background: var(--neutral-100, #f5f5f5);
}
.tiptap-editor-root table .selectedCell::after {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--color-primary, #fe3448);
  opacity: 0.08;
  pointer-events: none;
}
.tiptap-editor-root table .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--color-primary, #fe3448);
  cursor: col-resize;
  opacity: 0;
  transition: opacity 0.15s;
}
.tiptap-editor-root table .column-resize-handle:hover,
.tiptap-editor-root table:hover .column-resize-handle {
  opacity: 0.4;
}
.tiptap-editor-root ul[data-type="taskList"] {
  list-style: none;
  padding-left: 0;
}
.tiptap-editor-root ul[data-type="taskList"] li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.tiptap-editor-root ul[data-type="taskList"] li > label {
  margin-top: 2px;
  flex-shrink: 0;
}
.tiptap-editor-root ul[data-type="taskList"] li > label input[type="checkbox"] {
  cursor: pointer;
  width: 15px;
  height: 15px;
  accent-color: var(--color-primary, #fe3448);
}
.tiptap-editor-root ul[data-type="taskList"] li > div {
  flex: 1;
}
.tiptap-editor-root ul[data-type="taskList"] li[data-checked="true"] > div {
  text-decoration: line-through;
  opacity: 0.6;
}
.tiptap-math-inline {
  display: inline-block;
  padding: 0 2px;
  border-radius: 3px;
  cursor: pointer;
  vertical-align: middle;
}
.tiptap-math-inline:hover {
  background: var(--neutral-100, #f5f5f5);
}
.tiptap-math-block {
  display: block;
  padding: 12px;
  margin: 1rem 0;
  border-radius: 6px;
  border: 1px solid var(--neutral-300, #d4d4d4);
  background: var(--neutral-50, #fafafa);
  cursor: pointer;
  text-align: center;
}
.tiptap-math-block:hover {
  border-color: var(--color-primary, #fe3448);
  background: var(--neutral-100, #f5f5f5);
}
/* ── Inline code ── */
.tiptap-editor-root .ProseMirror code {
  background-color: var(--neutral-200, #e5e5e5);
  padding: 0.1em 0.4em;
  border-radius: 3px;
  font-size: 0.85em;
  color: #e11d48;
  font-family: "Source Code Pro", "JetBrains Mono", "Fira Code", monospace;
  font-weight: normal;
}
.dark .tiptap-editor-root .ProseMirror code {
  color: #fb7185;
}
/* ── Editor typography — matches Monaco editor font/spacing/tokens ── */
.tiptap-editor-root .ProseMirror {
  font-family: 'Inter', 'Open Sans', system-ui, -apple-system, sans-serif;
  font-size: 15px;
  line-height: 1.75;
  letter-spacing: 0.2px;
  color: var(--neutral-900);
  background: var(--surface-panel);
  caret-color: var(--color-primary, #fe3448);
}
.tiptap-editor-root .ProseMirror:focus { outline: none; }
/* Placeholder */
.tiptap-editor-root .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  color: var(--neutral-400);
  pointer-events: none;
  float: left;
  height: 0;
}
/* Paragraphs */
.tiptap-editor-root .ProseMirror p {
  margin-bottom: 1rem;
  line-height: 1.75;
  color: var(--neutral-700, var(--neutral-900));
}
/* Headings */
.tiptap-editor-root .ProseMirror h1 {
  font-size: 2rem; font-weight: 700; line-height: 1.2; letter-spacing: -0.02em;
  color: var(--neutral-950, var(--neutral-900));
  margin-top: 2.5rem; margin-bottom: 1rem;
}
.tiptap-editor-root .ProseMirror h2 {
  font-size: 1.5rem; font-weight: 700; line-height: 1.3;
  color: var(--neutral-950, var(--neutral-900));
  margin-top: 2rem; margin-bottom: 0.75rem;
}
.tiptap-editor-root .ProseMirror h3 {
  font-size: 1.25rem; font-weight: 600; line-height: 1.4;
  color: var(--neutral-950, var(--neutral-900));
  margin-top: 1.75rem; margin-bottom: 0.5rem;
}
.tiptap-editor-root .ProseMirror h4 {
  font-size: 1.1rem; font-weight: 600; line-height: 1.4;
  color: var(--neutral-900);
  margin-top: 1.5rem; margin-bottom: 0.5rem;
}
.tiptap-editor-root .ProseMirror h5 {
  font-size: 1rem; font-weight: 600; line-height: 1.4;
  color: var(--neutral-900);
  margin-top: 1.25rem; margin-bottom: 0.4rem;
}
.tiptap-editor-root .ProseMirror h6 {
  font-size: 0.9rem; font-weight: 600;
  color: var(--neutral-700);
  margin-top: 1rem; margin-bottom: 0.4rem;
}
/* Links */
.tiptap-editor-root .ProseMirror a {
  color: var(--color-accent, #0091AD);
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;
  cursor: pointer;
}
.tiptap-editor-root .ProseMirror a:hover { opacity: 0.8; }
/* Blockquote */
.tiptap-editor-root .ProseMirror blockquote {
  border-left: 4px solid var(--color-accent, #0091AD);
  padding: 0.5rem 0 0.5rem 1.5rem;
  margin: 1.5rem 0;
  color: var(--neutral-700);
  font-style: italic;
  border-radius: 0 4px 4px 0;
}
.tiptap-editor-root .ProseMirror blockquote p { margin-bottom: 0; }
/* Lists */
.tiptap-editor-root .ProseMirror ul { list-style-type: disc; padding-left: 1.5rem; margin: 1rem 0; }
.tiptap-editor-root .ProseMirror ol { list-style-type: decimal; padding-left: 1.5rem; margin: 1rem 0; }
.tiptap-editor-root .ProseMirror li { margin-bottom: 0.4rem; color: var(--neutral-700, var(--neutral-900)); }
.tiptap-editor-root .ProseMirror li > p { margin-bottom: 0; }
/* Inline marks */
.tiptap-editor-root .ProseMirror strong { font-weight: 700; color: var(--neutral-900); }
.tiptap-editor-root .ProseMirror em { font-style: italic; color: var(--neutral-800, var(--neutral-700)); }
.tiptap-editor-root .ProseMirror s,
.tiptap-editor-root .ProseMirror del { text-decoration: line-through; color: var(--neutral-600); }
.tiptap-editor-root .ProseMirror mark { background: #fef08a; color: inherit; border-radius: 2px; padding: 0 2px; }
/* HR */
.tiptap-editor-root .ProseMirror hr {
  border: none;
  border-top: 2px solid var(--neutral-300);
  margin: 2rem 0;
}
/* Images */
.tiptap-editor-root .ProseMirror img {
  max-width: 100%; height: auto; border-radius: 0.5rem; margin: 1.5rem 0; display: block;
}
/* ── Code block NodeView pre area ── */
.tiptap-code-block-pre {
  margin: 0 !important;
  padding: 0.75rem 1rem !important;
  overflow-x: auto;
  font-size: 0.875rem !important;
  line-height: 1.6 !important;
  font-family: "Source Code Pro", "JetBrains Mono", "Fira Code", monospace !important;
  border-radius: 0 !important;
}
/* lowlight token colours (light) */
.tiptap-code-block-pre .hljs-keyword,
.tiptap-code-block-pre .hljs-selector-tag,
.tiptap-code-block-pre .hljs-built_in { color: #a626a4; }
.tiptap-code-block-pre .hljs-string,
.tiptap-code-block-pre .hljs-attr { color: #50a14f; }
.tiptap-code-block-pre .hljs-number,
.tiptap-code-block-pre .hljs-literal { color: #986801; }
.tiptap-code-block-pre .hljs-comment { color: #a0a1a7; font-style: italic; }
.tiptap-code-block-pre .hljs-title,
.tiptap-code-block-pre .hljs-name { color: #4078f2; }
.tiptap-code-block-pre .hljs-variable,
.tiptap-code-block-pre .hljs-params { color: #383a42; }
/* lowlight token colours (dark) */
.dark .tiptap-code-block-pre .hljs-keyword,
.dark .tiptap-code-block-pre .hljs-selector-tag,
.dark .tiptap-code-block-pre .hljs-built_in { color: #c678dd; }
.dark .tiptap-code-block-pre .hljs-string,
.dark .tiptap-code-block-pre .hljs-attr { color: #98c379; }
.dark .tiptap-code-block-pre .hljs-number,
.dark .tiptap-code-block-pre .hljs-literal { color: #d19a66; }
.dark .tiptap-code-block-pre .hljs-comment { color: #5c6370; font-style: italic; }
.dark .tiptap-code-block-pre .hljs-title,
.dark .tiptap-code-block-pre .hljs-name { color: #61aeee; }
.dark .tiptap-code-block-pre .hljs-variable,
.dark .tiptap-code-block-pre .hljs-params { color: #abb2bf; }
`;

/* ─────────────────────────────────────────────────────────────────────────────
   Code block components (matching MarkdownRenderer UI)
   ───────────────────────────────────────────────────────────────────────────── */

function CopyButton({ getText }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-1.5 bg-(--neutral-300) text-(--neutral-800) px-2 py-1 rounded-md hover:bg-(--neutral-400) transition-colors"
      title="Copy code"
    >
      {copied ? <Check size={14} /> : <Clipboard size={14} />}
    </button>
  );
}

function CodeBlockNodeView({ node, updateAttributes }) {
  const language = node.attrs.language || "text";
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains("dark")),
    );
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);

  return (
    <NodeViewWrapper className="my-4 rounded-lg overflow-hidden not-prose">
      <div
        contentEditable={false}
        className="bg-(--neutral-200) text-(--neutral-800) flex justify-between items-center px-4 py-2"
      >
        <span className="text-sm font-semibold select-none">
          {language || "text"}
        </span>
        <CopyButton getText={() => node.textContent} />
      </div>
      <NodeViewContent
        as="pre"
        className="tiptap-code-block-pre"
        style={{
          background: isDark ? "#282c34" : "#f5f5f5",
          color: isDark ? "#abb2bf" : "#383a42",
        }}
      />
    </NodeViewWrapper>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   KaTeX renderer helper
   ───────────────────────────────────────────────────────────────────────────── */

let katexLib = null;
async function loadKatex() {
  if (katexLib) return katexLib;
  try {
    const mod = await import("katex");
    katexLib = mod.default || mod;
  } catch {
    katexLib = null;
  }
  return katexLib;
}

function KatexRenderer({ formula, displayMode = false, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    loadKatex().then((katex) => {
      if (!katex || !ref.current) return;
      try {
        katex.render(formula || "", ref.current, {
          displayMode,
          throwOnError: false,
          errorColor: "#ef4444",
        });
      } catch {
        if (ref.current) ref.current.textContent = formula || "";
      }
    });
  }, [formula, displayMode]);
  return <span ref={ref} className={className} />;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Custom Math Nodes
   ───────────────────────────────────────────────────────────────────────────── */

function MathInlineView({ node, updateAttributes }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(node.attrs.formula || "");
  const inputRef = useRef(null);

  const commit = () => {
    updateAttributes({ formula: draft });
    setEditing(false);
  };

  if (editing) {
    return (
      <NodeViewWrapper as="span" className="inline-block align-middle mx-1">
        <span className="inline-flex items-center gap-1 border border-(--color-primary) rounded px-1.5 py-0.5 bg-(--surface-base)">
          <input
            ref={inputRef}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") { setDraft(node.attrs.formula); setEditing(false); }
            }}
            className="text-sm bg-transparent outline-none w-48 font-mono"
            placeholder="LaTeX formula…"
          />
          <button type="button" onClick={commit} className="text-(--color-primary) hover:opacity-75">
            <Check className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={() => { setDraft(node.attrs.formula); setEditing(false); }} className="text-(--neutral-500) hover:opacity-75">
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="span" className="inline-block align-middle mx-0.5">
      <span
        className="tiptap-math-inline"
        title="Click to edit formula"
        onClick={() => { setDraft(node.attrs.formula || ""); setEditing(true); }}
      >
        <KatexRenderer formula={node.attrs.formula || ""} displayMode={false} />
      </span>
    </NodeViewWrapper>
  );
}

function MathBlockView({ node, updateAttributes }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(node.attrs.formula || "");

  const commit = () => {
    updateAttributes({ formula: draft });
    setEditing(false);
  };

  if (editing) {
    return (
      <NodeViewWrapper className="my-3">
        <div className="border border-(--color-primary) rounded-lg p-3 bg-(--surface-base)">
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); commit(); }
              if (e.key === "Escape") { setDraft(node.attrs.formula); setEditing(false); }
            }}
            rows={3}
            className="w-full text-sm font-mono bg-transparent outline-none resize-none"
            placeholder="LaTeX formula… (Ctrl+Enter to save)"
          />
          {draft && (
            <div className="mt-2 pt-2 border-t border-(--neutral-200) text-center">
              <KatexRenderer formula={draft} displayMode={true} />
            </div>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={() => { setDraft(node.attrs.formula); setEditing(false); }} className="text-xs text-(--neutral-500) hover:text-(--neutral-700)">Cancel</button>
            <button type="button" onClick={commit} className="text-xs text-(--color-primary) font-medium hover:opacity-75">Save (Ctrl+Enter)</button>
          </div>
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper className="my-3">
      <div
        className="tiptap-math-block"
        title="Click to edit formula"
        onClick={() => { setDraft(node.attrs.formula || ""); setEditing(true); }}
      >
        {node.attrs.formula
          ? <KatexRenderer formula={node.attrs.formula} displayMode={true} />
          : <span className="text-(--neutral-400) text-sm italic">Click to enter formula</span>
        }
      </div>
    </NodeViewWrapper>
  );
}

const MathInlineNode = Node.create({
  name: "mathInline",
  group: "inline",
  inline: true,
  atom: true,
  addAttributes() {
    return { formula: { default: "" } };
  },
  parseHTML() {
    return [{ tag: 'span[data-math-inline]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { "data-math-inline": "" })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(MathInlineView);
  },
});

const MathBlockNode = Node.create({
  name: "mathBlock",
  group: "block",
  atom: true,
  addAttributes() {
    return { formula: { default: "" } };
  },
  parseHTML() {
    return [{ tag: 'div[data-math-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-math-block": "" })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(MathBlockView);
  },
});

/* ─────────────────────────────────────────────────────────────────────────────
   Markdown ↔ HTML helpers
   ───────────────────────────────────────────────────────────────────────────── */

function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeMarkdown(value = "") {
  return String(value).replace(/([`*_{}\[\]()#+\-.!|>~])/g, "\\$1");
}

function applyInlineMarkdown(text = "") {
  // Extract math before escaping
  const mathParts = [];
  let processed = text.replace(/\$([^$\n]+)\$/g, (_, f) => {
    mathParts.push(f);
    return `\x00m${mathParts.length - 1}\x00`;
  });

  let html = escapeHtml(processed);
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  html = html.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, "$1<em>$2</em>");
  html = html.replace(/(^|[^_])_([^_]+)_(?!_)/g, "$1<em>$2</em>");

  // Restore math as custom nodes
  html = html.replace(/\x00m(\d+)\x00/g, (_, idx) => {
    const f = mathParts[parseInt(idx)] || "";
    return `<span data-math-inline="" data-formula="${escapeHtml(f)}"></span>`;
  });

  return html;
}

function parseTableRow(line) {
  return line
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

export function markdownToHtml(md = "") {
  if (!md) return "<p></p>";
  const lines = String(md).replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (!line.trim()) { i += 1; continue; }

    // Block math $$...$$
    if (/^\$\$\s*$/.test(line.trim())) {
      i += 1;
      const formulaLines = [];
      while (i < lines.length && !/^\$\$\s*$/.test(lines[i].trim())) {
        formulaLines.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) i += 1;
      blocks.push(`<div data-math-block="" data-formula="${escapeHtml(formulaLines.join("\n"))}"></div>`);
      continue;
    }

    // Fenced code
    const fence = line.match(/^```([a-zA-Z0-9_-]+)?\s*$/);
    if (fence) {
      const lang = fence[1] || "";
      i += 1;
      const codeLines = [];
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        codeLines.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) i += 1;
      blocks.push(`<pre><code class="language-${lang}">${escapeHtml(codeLines.join("\n"))}</code></pre>`);
      continue;
    }

    // HR
    if (/^(---|\*\*\*)\s*$/.test(line.trim())) {
      blocks.push("<hr />");
      i += 1;
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      blocks.push(`<h${heading[1].length}>${applyInlineMarkdown(heading[2])}</h${heading[1].length}>`);
      i += 1;
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const quoteLines = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^>\s?/, ""));
        i += 1;
      }
      blocks.push(`<blockquote><p>${applyInlineMarkdown(quoteLines.join(" "))}</p></blockquote>`);
      continue;
    }

    // Pipe table — header row | separator row | data rows
    if (/^\|/.test(line) && i + 1 < lines.length && /^\|[-: |]+\|$/.test(lines[i + 1])) {
      const headers = parseTableRow(line);
      i += 2; // skip separator
      const rows = [];
      while (i < lines.length && /^\|/.test(lines[i])) {
        rows.push(parseTableRow(lines[i]));
        i += 1;
      }
      const thead = `<thead><tr>${headers.map((h) => `<th>${applyInlineMarkdown(h)}</th>`).join("")}</tr></thead>`;
      const tbody = rows.length
        ? `<tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${applyInlineMarkdown(c)}</td>`).join("")}</tr>`).join("")}</tbody>`
        : "";
      blocks.push(`<table>${thead}${tbody}</table>`);
      continue;
    }

    // Task list
    if (/^\s*[-*]\s+\[[ xX]\]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+\[[ xX]\]\s/.test(lines[i])) {
        const checked = /^\s*[-*]\s+\[[xX]\]\s/.test(lines[i]);
        const text = lines[i].replace(/^\s*[-*]\s+\[[ xX]\]\s*/, "");
        items.push({ checked, text });
        i += 1;
      }
      const listItems = items
        .map(({ checked, text }) =>
          `<li data-checked="${checked}" data-type="taskItem"><label><input type="checkbox" ${checked ? "checked" : ""} /></label><div><p>${applyInlineMarkdown(text)}</p></div></li>`,
        )
        .join("");
      blocks.push(`<ul data-type="taskList">${listItems}</ul>`);
      continue;
    }

    // Bullet list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      blocks.push(`<ul>${items.map((item) => `<li><p>${applyInlineMarkdown(item)}</p></li>`).join("")}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push(`<ol>${items.map((item) => `<li><p>${applyInlineMarkdown(item)}</p></li>`).join("")}</ol>`);
      continue;
    }

    // Paragraph
    const paragraph = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^```/.test(lines[i]) &&
      !/^\$\$\s*$/.test(lines[i].trim()) &&
      !/^(---|\*\*\*)\s*$/.test(lines[i].trim()) &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !/^\|/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i])
    ) {
      paragraph.push(lines[i]);
      i += 1;
    }
    if (paragraph.length)
      blocks.push(`<p>${applyInlineMarkdown(paragraph.join(" "))}</p>`);
  }
  return blocks.join("\n") || "<p></p>";
}

/* Split a markdown string into its YAML front matter and body.
   The YAML block (if present) is everything between the opening and closing ---
   on their own lines at the very start of the document. */
function splitYamlFrontMatter(md = "") {
  const match = String(md).match(/^(---[\s\S]*?---[ \t]*\n?)/);
  if (match) return { yaml: match[1], body: md.slice(match[1].length) };
  return { yaml: "", body: md };
}

export function htmlToMarkdown(html = "") {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");

  const toMd = (node, parentTag = "") => {
    if (!node) return "";
    if (node.nodeType === 3 /* TEXT_NODE */) return node.nodeValue || "";
    if (node.nodeType !== 1 /* ELEMENT_NODE */) return "";

    const tag = node.tagName.toLowerCase();
    const children = Array.from(node.childNodes).map((child) => toMd(child, tag)).join("");

    switch (tag) {
      case "strong":
      case "b":
        return `**${children}**`;
      case "em":
      case "i":
        return `*${children}*`;
      case "u":
        return `<u>${children}</u>`;
      case "s":
      case "strike":
      case "del":
        return `~~${children}~~`;
      case "code":
        return parentTag === "pre" ? children : `\`${children}\``;
      case "mark":
        return `==${children}==`;
      case "a": {
        const href = node.getAttribute("href") || "";
        return `[${children || href}](${href})`;
      }
      case "img": {
        const alt = node.getAttribute("alt") || "";
        let src = node.getAttribute("src") || "";
        try {
          const url = new URL(src, window.location.origin);
          url.search = "";
          src = /^https?:/i.test(src) ? url.toString() : url.pathname;
        } catch {
          src = String(src).split("?")[0];
        }
        return `![${escapeMarkdown(alt)}](${src})`;
      }
      case "hr":
        return "\n---\n\n";
      case "br":
        return "  \n";
      case "h1":
      case "h2":
      case "h3":
      case "h4":
      case "h5":
      case "h6":
        return `${"#".repeat(Number(tag.slice(1)))} ${children.trim()}\n\n`;
      case "p":
        return parentTag === "li" || parentTag === "div" ? children.trim() : `${children.trim()}\n\n`;
      case "blockquote": {
        const text = children.trim();
        return text
          ? `${text.split("\n").map((l) => `> ${l}`).join("\n")}\n\n`
          : "";
      }
      case "ul": {
        if (node.getAttribute("data-type") === "taskList") {
          const items = Array.from(node.children).filter(
            (li) => li.tagName.toLowerCase() === "li",
          );
          return `${items.map((li) => {
            const checked = li.getAttribute("data-checked") === "true";
            const text = toMd(li).trim();
            return `- [${checked ? "x" : " "}] ${text}`;
          }).join("\n")}\n\n`;
        }
        return `${Array.from(node.children)
          .filter((li) => li.tagName.toLowerCase() === "li")
          .map((li) => `- ${toMd(li).trim()}`)
          .join("\n")}\n\n`;
      }
      case "ol":
        return `${Array.from(node.children)
          .filter((li) => li.tagName.toLowerCase() === "li")
          .map((li, idx) => `${idx + 1}. ${toMd(li).trim()}`)
          .join("\n")}\n\n`;
      case "li": {
        // task item: skip label (checkbox), render div content
        const label = node.querySelector("label");
        const div = node.querySelector("div");
        if (label && div) return toMd(div).trim();
        return children.trim();
      }
      case "label":
        return ""; // skip checkbox labels in task items
      case "input":
        return ""; // skip checkboxes
      case "pre": {
        const codeNode = node.querySelector("code");
        const code = (codeNode?.textContent || node.textContent || "").replace(/\n$/, "");
        const className = codeNode?.getAttribute("class") || "";
        const langMatch = className.match(/language-([a-zA-Z0-9_-]+)/);
        return `\`\`\`${langMatch?.[1] || ""}\n${code}\n\`\`\`\n\n`;
      }
      case "table": {
        // TipTap renders all rows (including <th> header rows) inside a single
        // <tbody> — there is never a <thead> element in the output.
        const allRows = Array.from(node.querySelectorAll("tr"));
        if (!allRows.length) return children;
        const headerRow = allRows[0];
        const dataRows = allRows.slice(1);
        const headers = Array.from(headerRow.querySelectorAll("th,td")).map(
          (cell) => toMd(cell).trim(),
        );
        const separator = headers.map(() => "---").join(" | ");
        const headerLine = `| ${headers.join(" | ")} |`;
        const separatorLine = `| ${separator} |`;
        const bodyLines = dataRows.map((row) => {
          const cells = Array.from(row.querySelectorAll("td,th")).map(
            (cell) => toMd(cell).trim(),
          );
          return `| ${cells.join(" | ")} |`;
        });
        return `${headerLine}\n${separatorLine}\n${bodyLines.join("\n")}\n\n`;
      }
      case "thead":
      case "tbody":
      case "tr":
      case "th":
      case "td":
        return children;
      // Math nodes
      case "span": {
        if (node.hasAttribute("data-math-inline")) {
          const formula = node.getAttribute("data-formula") || "";
          return `$${formula}$`;
        }
        return children;
      }
      case "div": {
        if (node.hasAttribute("data-math-block")) {
          const formula = node.getAttribute("data-formula") || "";
          return `$$\n${formula}\n$$\n\n`;
        }
        return children;
      }
      default:
        return children;
    }
  };
  return toMd(doc.body).replace(/\n{3,}/g, "\n\n").trim();
}

/* ─────────────────────────────────────────────────────────────────────────────
   Toolbar primitives
   ───────────────────────────────────────────────────────────────────────────── */

export function ToolbarButton({ onClick, isActive, disabled, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "flex items-center justify-center w-8 h-8 rounded-md transition-colors text-(--neutral-600)",
        "hover:bg-(--neutral-100) hover:text-(--neutral-900)",
        "disabled:opacity-40 disabled:pointer-events-none",
        isActive &&
          "bg-(--color-primary)/10 text-(--color-primary) hover:bg-(--color-primary)/20 hover:text-(--color-primary)",
      )}
    >
      {children}
    </button>
  );
}

export function ToolbarDivider() {
  return <div className="w-px h-5 bg-(--neutral-200) mx-1" />;
}

/* ─────────────────────────────────────────────────────────────────────────────
   LinkPanel — floating input replacing window.prompt
   ───────────────────────────────────────────────────────────────────────────── */

function LinkPanel({ editor, onClose }) {
  const [url, setUrl] = useState(editor?.getAttributes("link").href || "");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

  const apply = () => {
    if (!editor) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
    }
    onClose();
  };

  return (
    <div className="absolute z-50 top-full left-0 mt-1 w-80 rounded-lg border border-(--neutral-300) bg-(--surface-base) shadow-lg p-3 flex flex-col gap-2">
      <p className="text-xs font-medium text-(--neutral-700)">Insert / edit link</p>
      <input
        ref={inputRef}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); apply(); }
          if (e.key === "Escape") { e.preventDefault(); onClose(); }
        }}
        placeholder="https://example.com"
        className="w-full text-sm px-2 py-1.5 rounded border border-(--neutral-300) bg-(--surface-base) text-(--neutral-900) outline-none focus:border-(--color-primary)"
      />
      <div className="flex gap-2 justify-end">
        {editor?.isActive("link") && (
          <button
            type="button"
            onClick={() => { editor.chain().focus().extendMarkRange("link").unsetLink().run(); onClose(); }}
            className="text-xs text-red-500 hover:text-red-700 mr-auto"
          >
            Remove link
          </button>
        )}
        <button type="button" onClick={onClose} className="text-xs text-(--neutral-500) hover:text-(--neutral-700)">Cancel</button>
        <button type="button" onClick={apply} className="text-xs text-(--color-primary) font-medium hover:opacity-75">Apply</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MathPanel — insert math formula
   ───────────────────────────────────────────────────────────────────────────── */

function MathPanel({ editor, onClose }) {
  const [formula, setFormula] = useState("");
  const [mode, setMode] = useState("inline"); // "inline" | "block"
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const apply = () => {
    if (!editor || !formula.trim()) return;
    if (mode === "inline") {
      editor.chain().focus().insertContent({
        type: "mathInline",
        attrs: { formula: formula.trim() },
      }).run();
    } else {
      editor.chain().focus().insertContent([
        { type: "mathBlock", attrs: { formula: formula.trim() } },
        { type: "paragraph" },
      ]).run();
    }
    onClose();
  };

  return (
    <div className="absolute z-50 top-full left-0 mt-1 w-80 rounded-lg border border-(--neutral-300) bg-(--surface-base) shadow-lg p-3 flex flex-col gap-2">
      <p className="text-xs font-medium text-(--neutral-700)">Insert math formula</p>
      <div className="flex gap-2 text-xs">
        <button
          type="button"
          onClick={() => setMode("inline")}
          className={cn("px-2 py-0.5 rounded", mode === "inline" ? "bg-(--color-primary)/10 text-(--color-primary)" : "text-(--neutral-500)")}
        >Inline ($…$)</button>
        <button
          type="button"
          onClick={() => setMode("block")}
          className={cn("px-2 py-0.5 rounded", mode === "block" ? "bg-(--color-primary)/10 text-(--color-primary)" : "text-(--neutral-500)")}
        >Block ($$…$$)</button>
      </div>
      <textarea
        ref={inputRef}
        value={formula}
        onChange={(e) => setFormula(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); apply(); }
          if (e.key === "Escape") { e.preventDefault(); onClose(); }
        }}
        rows={3}
        className="w-full text-sm font-mono px-2 py-1.5 rounded border border-(--neutral-300) bg-(--surface-base) text-(--neutral-900) outline-none focus:border-(--color-primary) resize-none"
        placeholder="e.g. x^2 + y^2 = r^2"
      />
      {formula && (
        <div className="border border-(--neutral-200) rounded p-2 text-center min-h-10">
          <KatexRenderer formula={formula} displayMode={mode === "block"} />
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="text-xs text-(--neutral-500) hover:text-(--neutral-700)">Cancel</button>
        <button type="button" onClick={apply} disabled={!formula.trim()} className="text-xs text-(--color-primary) font-medium hover:opacity-75 disabled:opacity-40">Insert</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   TableOpsBar — contextual table operations
   ───────────────────────────────────────────────────────────────────────────── */

function TableOpsBar({ editor }) {
  if (!editor || !editor.isActive("table")) return null;

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 bg-(--neutral-50) border-b border-(--neutral-200) text-xs flex-wrap">
      <span className="text-(--neutral-400) mr-1 text-xs font-medium">Table:</span>
      <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row below" className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-(--neutral-200) text-(--neutral-600)">
        <Rows3 className="w-3 h-3" /><Plus className="w-2.5 h-2.5" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().deleteRow().run()} title="Delete row" className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-(--neutral-200) text-(--neutral-600)">
        <Rows3 className="w-3 h-3" /><Minus className="w-2.5 h-2.5" />
      </button>
      <div className="w-px h-3.5 bg-(--neutral-300) mx-0.5" />
      <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column right" className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-(--neutral-200) text-(--neutral-600)">
        <Columns3 className="w-3 h-3" /><Plus className="w-2.5 h-2.5" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()} title="Delete column" className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-(--neutral-200) text-(--neutral-600)">
        <Columns3 className="w-3 h-3" /><Minus className="w-2.5 h-2.5" />
      </button>
      <div className="w-px h-3.5 bg-(--neutral-300) mx-0.5" />
      <button type="button" onClick={() => editor.chain().focus().toggleHeaderRow().run()} title="Toggle header row" className="px-1.5 py-0.5 rounded hover:bg-(--neutral-200) text-(--neutral-600)">
        Header
      </button>
      <button type="button" onClick={() => editor.chain().focus().mergeCells().run()} title="Merge selected cells" className="px-1.5 py-0.5 rounded hover:bg-(--neutral-200) text-(--neutral-600)">
        Merge
      </button>
      <button type="button" onClick={() => editor.chain().focus().splitCell().run()} title="Split cell" className="px-1.5 py-0.5 rounded hover:bg-(--neutral-200) text-(--neutral-600)">
        Split
      </button>
      <div className="w-px h-3.5 bg-(--neutral-300) mx-0.5" />
      <button type="button" onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table" className="px-1.5 py-0.5 rounded hover:bg-red-100 text-red-500">
        Delete table
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SimpleToolbar
   ───────────────────────────────────────────────────────────────────────────── */

export function SimpleToolbar({ editor, onImageUpload, onDeleteImage, isUploading }) {
  const fileInputRef = useRef(null);
  const [showLinkPanel, setShowLinkPanel] = useState(false);
  const [showMathPanel, setShowMathPanel] = useState(false);
  const linkBtnRef = useRef(null);
  const mathBtnRef = useRef(null);

  const deleteSelectedImage = useCallback(() => {
    if (!editor || !editor.isActive("image")) return;
    const src = editor.getAttributes("image")?.src;
    editor.chain().focus().deleteSelection().run();
    if (src && typeof onDeleteImage === "function") onDeleteImage(src);
  }, [editor, onDeleteImage]);

  if (!editor) return null;

  const handleImagePick = async (event) => {
    if (typeof onImageUpload !== "function") {
      if (event?.target) event.target.value = "";
      return;
    }
    const selection = editor.state.selection;
    const uploaded = await onImageUpload(event, { mode: "tiptap" });
    if (!Array.isArray(uploaded) || uploaded.length === 0) return;
    editor.chain().focus().setTextSelection(selection).run();
    for (const img of uploaded) {
      editor.chain().focus().insertContent([
        { type: "image", attrs: { src: img.url, alt: `Image ${img.order || ""}`.trim() } },
        { type: "paragraph" },
      ]).run();
    }
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-0.5 px-3 py-2 border-b border-(--neutral-200) bg-(--surface-panel) flex-wrap">
        {/* Undo/Redo */}
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (⌘Z)">
          <Undo2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo (⌘⇧Z)">
          <Redo2 className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Text formatting */}
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive("bold")} title="Bold (⌘B)">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive("italic")} title="Italic (⌘I)">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive("underline")} title="Underline (⌘U)">
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive("strike")} title="Strikethrough">
          <Strikethrough className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCode().run()} isActive={editor.isActive("code")} title="Inline Code">
          <Code className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Headings */}
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive("heading", { level: 1 })} title="Heading 1">
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive("heading", { level: 2 })} title="Heading 2">
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive("heading", { level: 3 })} title="Heading 3">
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} isActive={editor.isActive("heading", { level: 4 })} title="Heading 4">
          <Heading4 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()} isActive={editor.isActive("heading", { level: 5 })} title="Heading 5">
          <Heading5 className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive("bulletList")} title="Bullet List">
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive("orderedList")} title="Numbered List">
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleTaskList().run()} isActive={editor.isActive("taskList")} title="Task List">
          <ListChecks className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive("blockquote")} title="Blockquote">
          <Quote className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={editor.isActive("codeBlock")} title="Code Block">
          <Code className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("left").run()} isActive={editor.isActive({ textAlign: "left" })} title="Align Left">
          <AlignLeft className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("center").run()} isActive={editor.isActive({ textAlign: "center" })} title="Align Center">
          <AlignCenter className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("right").run()} isActive={editor.isActive({ textAlign: "right" })} title="Align Right">
          <AlignRight className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Table */}
        <ToolbarButton onClick={insertTable} isActive={editor.isActive("table")} title="Insert Table">
          <TableIcon className="w-4 h-4" />
        </ToolbarButton>

        {/* Math */}
        <div className="relative" ref={mathBtnRef}>
          <ToolbarButton
            onClick={() => { setShowMathPanel((v) => !v); setShowLinkPanel(false); }}
            isActive={showMathPanel}
            title="Insert Math (LaTeX)"
          >
            <span className="font-serif text-sm font-bold">∑</span>
          </ToolbarButton>
          {showMathPanel && (
            <MathPanel editor={editor} onClose={() => setShowMathPanel(false)} />
          )}
        </div>

        {/* Link */}
        <div className="relative" ref={linkBtnRef}>
          <ToolbarButton
            onClick={() => { setShowLinkPanel((v) => !v); setShowMathPanel(false); }}
            isActive={editor.isActive("link") || showLinkPanel}
            title="Insert / Edit Link"
          >
            <Link2 className="w-4 h-4" />
          </ToolbarButton>
          {showLinkPanel && (
            <LinkPanel editor={editor} onClose={() => setShowLinkPanel(false)} />
          )}
        </div>

        {/* Image */}
        <ToolbarButton onClick={() => fileInputRef.current?.click()} disabled={isUploading} title="Insert Image">
          <Image className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={deleteSelectedImage} disabled={!editor.isActive("image")} title="Delete Selected Image">
          <Trash2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
          <Minus className="w-4 h-4" />
        </ToolbarButton>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
          multiple
          onChange={handleImagePick}
          className="hidden"
        />
      </div>

      {/* Contextual table operations bar */}
      <TableOpsBar editor={editor} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   ImageNodeView
   ───────────────────────────────────────────────────────────────────────────── */

export function ImageNodeView({ node, deleteNode, editor }) {
  const [showOverlay, setShowOverlay] = useState(false);
  const [broken, setBroken] = useState(false);

  const handleDelete = () => {
    const src = node.attrs.src;
    if (src && editor && typeof editor.options?.onDeleteImage === "function") {
      editor.options.onDeleteImage(src);
    }
    deleteNode();
  };

  if (broken) {
    return (
      <NodeViewWrapper className="inline-block relative my-2">
        <div className="flex items-center gap-2 px-3 py-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950 text-sm text-amber-700 dark:text-amber-300">
          <span>⚠️ Image not found</span>
          <button onClick={handleDelete} className="ml-2 text-red-500 hover:text-red-700" title="Remove broken image">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      className="inline-block relative my-2 group"
      onMouseEnter={() => setShowOverlay(true)}
      onMouseLeave={() => setShowOverlay(false)}
    >
      <img
        src={node.attrs.src}
        alt={node.attrs.alt || ""}
        className="max-w-full h-auto rounded"
        onError={() => setBroken(true)}
      />
      {showOverlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
          <button
            onClick={handleDelete}
            className="p-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white"
            title="Delete image"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </NodeViewWrapper>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   TiptapEditor
   ───────────────────────────────────────────────────────────────────────────── */

export default function TiptapEditor({
  value,
  onChange,
  onImageUpload,
  onDeleteImage,
  isUploading,
}) {
  // YAML front matter is stripped before editing and re-prepended on save.
  const yamlRef = useRef("");
  const lastBodyRef = useRef(""); // markdown body without YAML

  const initContent = (() => {
    const { yaml, body } = splitYamlFrontMatter(value || "");
    yamlRef.current = yaml;
    lastBodyRef.current = body;
    return markdownToHtml(body);
  })();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      Underline,
      TiptapImage.extend({
        addNodeView() {
          return ReactNodeViewRenderer(ImageNodeView);
        },
      }).configure({ inline: false, allowBase64: false }),
      TiptapLink.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-(--color-primary) underline cursor-pointer" },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Highlight,
      CodeBlockLowlight.extend({
        addNodeView() {
          return ReactNodeViewRenderer(CodeBlockNodeView);
        },
      }).configure({ lowlight }),
      Placeholder.configure({ placeholder: "Start writing…" }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      TaskList,
      TaskItem.configure({ nested: true }),
      MathInlineNode,
      MathBlockNode,
    ],
    content: initContent,
    onDeleteImage,
    editorProps: {
      attributes: {
        class: "min-h-[300px] px-6 py-5",
      },
    },
    onUpdate({ editor: currentEditor }) {
      const body = htmlToMarkdown(currentEditor.getHTML());
      lastBodyRef.current = body;
      onChange(yamlRef.current + body);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const { yaml, body } = splitYamlFrontMatter(value || "");
    // Update stored YAML if it changed (e.g. external save/reload)
    yamlRef.current = yaml;
    // Only reset editor content when the body actually differs
    if (body === lastBodyRef.current) return;
    editor.commands.setContent(markdownToHtml(body), false);
    lastBodyRef.current = body;
  }, [value, editor]);

  return (
    <div className="flex flex-col h-full w-full bg-(--surface-panel) tiptap-editor-root">
      <style>{TABLE_STYLES}</style>
      <SimpleToolbar
        editor={editor}
        onImageUpload={onImageUpload}
        onDeleteImage={onDeleteImage}
        isUploading={isUploading}
      />
      <div className="flex-1 overflow-y-auto bg-(--surface-panel)">
        <EditorContent
          editor={editor}
          className="h-full [&_.ProseMirror]:min-h-full"
        />
      </div>
    </div>
  );
}
